import {
  Box,
  Button,
  Icon,
  Sidebar,
  Text,
  Title,
  Tag,
} from '@nimbus-ds/components';
import { ChevronLeftIcon, SearchIcon } from '@nimbus-ds/icons';
import { EmptyMessage } from '@nimbus-ds/patterns';
import { useTranslation } from 'react-i18next';
import type { WhatsappPurchasesListResponseDto } from '@tiendanube/common';
import { formatCurrency } from 'commons/utils';
import { dateFormat, Format } from 'commons/utils/date';
import useTranslationConfigurations from 'domains/Configurations/useTranslationConfigurations';

const getStatusAppearance = (status: string) => {
  switch (status) {
    case 'PAID':
      return 'success';
    case 'CANCELLED':
    case 'FAILED':
      return 'danger';
    case 'PENDING':
    case 'IN_PROCESS':
    default:
      return 'warning';
  }
};

const getStatusTextColor = (status: string) => {
  switch (status) {
    case 'PAID':
      return 'success-textLow';
    case 'CANCELLED':
    case 'FAILED':
      return 'danger-textLow';
    case 'PENDING':
    case 'IN_PROCESS':
    default:
      return 'warning-textLow';
  }
};

interface WhatsAppBannerHistorySidebarProps {
  isOpen: boolean;
  onClick: () => void;
  onRemove: () => void;
  purchasesData: WhatsappPurchasesListResponseDto | undefined;
}

export function WhatsAppBannerHistorySidebar({
  isOpen,
  onClick,
  onRemove,
  purchasesData,
}: Readonly<WhatsAppBannerHistorySidebarProps>) {
  const t = useTranslationConfigurations('notifications.whatsapp');

  const {
    i18n: { language },
  } = useTranslation();

  return (
    <Sidebar padding="base" open={isOpen} onRemove={onRemove}>
      <Box marginBottom="2">
        <Button
          appearance="transparent"
          onClick={onClick}
          aria-label={t('history.back')}
        >
          <Icon
            color="primary-textLow"
            source={<ChevronLeftIcon size="small" />}
          />
        </Button>
      </Box>

      <Sidebar.Header title={t('history.title')} />

      <Sidebar.Body>
        {(purchasesData?.items?.length ?? 0) === 0 ? (
          <Box width="100%" height="auto" paddingY="3">
            <EmptyMessage
              title={t('history.emptyTitle')}
              text={t('history.emptyText')}
              icon={<SearchIcon size={32} color="neutral-textHigh" />}
            />
          </Box>
        ) : (
          purchasesData?.items.map(
            (
              { id, status, created_at, credits, base_amount, base_currency },
              index,
            ) => {
              const lastItem = index === purchasesData?.items.length - 1;

              return (
                <Box
                  key={id}
                  width="100%"
                  height="auto"
                  borderStyle="solid"
                  borderColor="neutral-surfaceHighlight"
                  borderWidth="none"
                  borderTopWidth="1"
                  borderBottomWidth={lastItem ? '1' : 'none'}
                  paddingY="3"
                >
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Title as="h5">
                      {credits} {t('banner.creditsUnit')}
                    </Title>
                    <Text>
                      {formatCurrency(base_amount, base_currency, language)}
                    </Text>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    gap="0-5"
                  >
                    <Text fontSize="caption">
                      {dateFormat(created_at, Format.DD_MM_YYYY)}
                    </Text>
                    <Tag appearance={getStatusAppearance(status)}>
                      <Text
                        fontSize="caption"
                        color={getStatusTextColor(status)}
                      >
                        {t(`status.${status}`)}
                      </Text>
                    </Tag>
                  </Box>
                </Box>
              );
            },
          )
        )}
      </Sidebar.Body>
    </Sidebar>
  );
}
